import React, { useMemo, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Permission, UserRole } from '../types';
import { hasPermission, getPermissionDescription, getRoleDisplayName } from '../utils/permissions';
import LoadingSpinner from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  requireAll?: boolean; // If true, user must have ALL permissions; if false, user needs ANY permission
  fallbackPath?: string;
  fallbackComponent?: React.ReactNode;
  showAccessDeniedMessage?: boolean;
  customAccessDeniedMessage?: string;
  logAccessAttempts?: boolean;
}

// Access Denied Component
const AccessDeniedMessage: React.FC<{
  userRole: UserRole;
  requiredPermissions: Permission[];
  requireAll: boolean;
  customMessage?: string;
}> = ({ userRole, requiredPermissions, requireAll, customMessage }) => {
  const permissionText = useMemo(() => {
    if (customMessage) return customMessage;
    
    const permissionNames = requiredPermissions.map(getPermissionDescription).join(', ');
    const logicText = requireAll ? 'all of' : 'any of';
    
    return `You need ${logicText} the following permissions: ${permissionNames}. Your current role (${getRoleDisplayName(userRole)}) doesn't have sufficient access.`;
  }, [userRole, requiredPermissions, requireAll, customMessage]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gray-50"
      role="alert"
      aria-live="polite"
      aria-label="Access denied notification"
    >
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
          <svg 
            className="w-6 h-6 text-red-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Access Denied
        </h2>
        <p className="text-gray-600 mb-4 text-sm">
          {permissionText}
        </p>
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          aria-label="Go back to previous page"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  fallbackPath = '/dashboard',
  fallbackComponent,
  showAccessDeniedMessage = true,
  customAccessDeniedMessage,
  logAccessAttempts = true
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Memoize permission checking to avoid unnecessary recalculations
  const permissionCheck = useMemo(() => {
    if (!requiredPermission && !requiredPermissions) {
      return { hasAccess: true, permissions: [] };
    }

    const permissions = requiredPermissions || (requiredPermission ? [requiredPermission] : []);
    const hasAccess = requireAll 
      ? permissions.every(permission => hasPermission(user?.role || 'staff', permission))
      : permissions.some(permission => hasPermission(user?.role || 'staff', permission));

    return { hasAccess, permissions };
  }, [user?.role, requiredPermission, requiredPermissions, requireAll]);

  // Log access attempts for security monitoring
  const logAccessAttempt = useCallback((success: boolean, permissions: Permission[]) => {
    if (!logAccessAttempts || !user) return;

    const logData = {
      timestamp: new Date().toISOString(),
      userId: user.id,
      userEmail: user.email,
      userRole: user.role,
      path: location.pathname,
      success,
      requiredPermissions: permissions,
      requireAll,
      userAgent: navigator.userAgent
    };

    if (success) {
      console.log('✅ Access granted:', logData);
    } else {
      console.warn('❌ Access denied:', logData);
      // In production, you might want to send this to a logging service
      // Example: analytics.track('access_denied', logData);
    }
  }, [logAccessAttempts, user, location.pathname, requireAll]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        role="status"
        aria-label="Checking permissions"
      >
        <LoadingSpinner size="lg" text="Checking permissions..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    logAccessAttempt(false, permissionCheck.permissions);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if specified
  if (requiredPermission || requiredPermissions) {
    const { hasAccess, permissions } = permissionCheck;

    if (!hasAccess) {
      logAccessAttempt(false, permissions);

      if (fallbackComponent) {
        return <>{fallbackComponent}</>;
      }

      if (showAccessDeniedMessage) {
        return (
          <AccessDeniedMessage
            userRole={user.role}
            requiredPermissions={permissions}
            requireAll={requireAll}
            customMessage={customAccessDeniedMessage}
          />
        );
      }

      return <Navigate to={fallbackPath} replace />;
    }

    // Log successful access
    logAccessAttempt(true, permissions);
  }

  return <>{children}</>;
};

export default ProtectedRoute;
